import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faArrowLeft,
  faCheck,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import FormModal from '@frontend/Components/Modals/FormModal';
import MicrosoftHttpService from '@frontend/AppSettings/services/MicrosoftHttpService';
import { getSharePointConfig } from '@frontend/AppSettings/environmentConfig';
import type { SharePointFile } from './useTaskDocuments';

interface SharePointFileBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: SharePointFile[]) => void;
  isLinking: boolean;
}

const GRAPH_SCOPES = ['https://graph.microsoft.com/Files.Read.All'] as any[];

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const SharePointFileBrowser: React.FC<SharePointFileBrowserProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  isLinking,
}) => {
  const [items, setItems] = useState<SharePointFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SharePointFile[]>([]);
  const [folderStack, setFolderStack] = useState<{ id: string; name: string }[]>([]);

  const { siteId, driveId } = getSharePointConfig();

  const loadFolder = useCallback(
    async (folderId?: string) => {
      if (!siteId || !driveId) return;

      setIsLoading(true);
      try {
        const service = new MicrosoftHttpService(GRAPH_SCOPES);
        let result;
        if (folderId) {
          result = await service.getSharePointFolderChildren(siteId, driveId, folderId);
        } else {
          result = await service.getSharePointDriveRoot(siteId, driveId);
        }
        const fetched: SharePointFile[] = result?.value ?? result ?? [];
        setItems(fetched);
      } catch (err) {
        console.error('Error loading SharePoint folder:', err);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    },
    [siteId, driveId],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedFiles([]);
      setFolderStack([]);
      loadFolder();
    }
  }, [isOpen, loadFolder]);

  const handleFolderClick = (folder: SharePointFile) => {
    setFolderStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setSelectedFiles([]);
    loadFolder(folder.id);
  };

  const handleBack = () => {
    setFolderStack((prev) => {
      const next = prev.slice(0, -1);
      const parentId = next.length > 0 ? next[next.length - 1].id : undefined;
      loadFolder(parentId);
      return next;
    });
    setSelectedFiles([]);
  };

  const toggleFileSelection = (file: SharePointFile) => {
    setSelectedFiles((prev) => {
      const exists = prev.find((f) => f.id === file.id);
      if (exists) return prev.filter((f) => f.id !== file.id);
      return [...prev, file];
    });
  };

  const handleLinkSelected = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  };

  const breadcrumb = ['Root', ...folderStack.map((f) => f.name)].join(' / ');

  const folders = items.filter((item) => item.folder);
  const files = items.filter((item) => item.file);

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Browse SharePoint Files" size="large">
      <div className="sp-browser-container">
        <div className="sp-browser-toolbar">
          {folderStack.length > 0 && (
            <button type="button" className="sp-browser-back-btn" onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <span className="sp-browser-breadcrumb">{breadcrumb}</span>
        </div>

        <div className="sp-browser-list">
          {isLoading ? (
            <div className="sp-browser-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </div>
          ) : folders.length === 0 && files.length === 0 ? (
            <div className="sp-browser-empty">This folder is empty.</div>
          ) : (
            <>
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="sp-browser-item sp-browser-folder"
                  onClick={() => handleFolderClick(folder)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleFolderClick(folder); }}
                >
                  <FontAwesomeIcon icon={faFolder} className="sp-browser-item-icon sp-folder-icon" />
                  <span className="sp-browser-item-name">{folder.name}</span>
                </div>
              ))}
              {files.map((file) => {
                const isSelected = selectedFiles.some((f) => f.id === file.id);
                return (
                  <div
                    key={file.id}
                    className={`sp-browser-item sp-browser-file ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleFileSelection(file)}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFileSelection(file); } }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="sp-browser-checkbox"
                    />
                    <FontAwesomeIcon icon={faFile} className="sp-browser-item-icon sp-file-icon" />
                    <span className="sp-browser-item-name">{file.name}</span>
                    <span className="sp-browser-item-size">{formatFileSize(file.size)}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="sp-browser-footer">
          <span className="sp-browser-selected-count">
            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
          </span>
          <button
            type="button"
            className="sp-browser-link-btn"
            onClick={handleLinkSelected}
            disabled={selectedFiles.length === 0 || isLinking}
          >
            <FontAwesomeIcon icon={isLinking ? faSpinner : faCheck} spin={isLinking} />
            {isLinking ? 'Linking...' : 'Link Selected'}
          </button>
        </div>
      </div>
    </FormModal>
  );
};

export default SharePointFileBrowser;
